import { memo, useState, useEffect, useCallback } from 'react';
import Text from '@wconcept/wconcept-ui-renewal/components/atoms/Text';
import { classNameBind } from '@wconcept/wconcept-common/utils/commonUtils';
import * as S from './style';

interface IProps {
  desc?: string;
  targetDate: string;
  serverTimeOffset: number | null;
  onExpired?: (isExpired: boolean) => void;
  eventStatus?: '1' | '2' | '3'; // 1: 마감 | 2: 진행중 | 3: 진행 예정
  bgType?: 'gray' | 'black' | 'opacity' | string;
}

const CountdownTimer = ({
  desc,
  targetDate,
  serverTimeOffset,
  onExpired,
  eventStatus = '2',
  bgType = 'gray',
}: IProps) => {
  const cName = classNameBind([
    'countdown-timer',
    `event-status-${eventStatus}`,
    `bg-type-${bgType}`,
  ]);

  const [countdown, setCountdown] = useState({
    hours: '00',
    minutes: '00',
    seconds: '00',
  });

  const getAdjustedTime = useCallback(
    () => new Date(Date.now() + (serverTimeOffset ?? 0)),
    [serverTimeOffset]
  );

  const formatTime = useCallback(
    (value: number): string => value.toString().padStart(2, '0'),
    []
  );

  const calculateCountdown = useCallback(() => {
    if (!targetDate) {
      setCountdown({ hours: '00', minutes: '00', seconds: '00' });
      if (onExpired) onExpired(true);
      return;
    }

    const now = getAdjustedTime();
    const target = new Date(targetDate);
    const diff = target.getTime() - now.getTime();

    if (diff <= 0) {
      if (onExpired) onExpired(true);
      setCountdown({ hours: '00', minutes: '00', seconds: '00' });
    } else {
      if (onExpired) onExpired(false);
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      setCountdown({
        hours: formatTime(hours),
        minutes: formatTime(minutes),
        seconds: formatTime(seconds),
      });
    }
  }, [targetDate, getAdjustedTime, formatTime, onExpired]);

  useEffect(() => {
    if (serverTimeOffset === null) return;
    calculateCountdown();
    const interval = window.setInterval(calculateCountdown, 1000);
    // eslint-disable-next-line consistent-return
    return () => window.clearInterval(interval);
  }, [targetDate, calculateCountdown, serverTimeOffset]);

  const getDisplayContent = () => {
    if (eventStatus === '1') {
      return '종료';
    }
    if (eventStatus === '3') {
      return '진행예정';
    }
    return `<em>${countdown.hours}:${countdown.minutes}:${countdown.seconds}</em>남음`;
  };

  return (
    <S.CountdownTimer className={cName}>
      <Text className="status-text" ellipsis={1}>
        {desc}
      </Text>
      <Text weight={500} className="countdown">
        {getDisplayContent()}
      </Text>
    </S.CountdownTimer>
  );
};

export default memo(CountdownTimer);
